import axios from 'axios';
import { Repository, AnalysisResult } from '../types';

const API_BASE_URL = import.meta.env.VITE_API_URL || 'http://localhost:8000';

// Create axios instance with default config
export const apiClient = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// Add request interceptor for debugging
apiClient.interceptors.request.use(
  (config) => {
    console.log('API Request:', config.method?.toUpperCase(), config.url);
    console.log('Request headers:', config.headers);
    return config;
  },
  (error) => {
    console.error('Request error:', error);
    return Promise.reject(error);
  }
);

// Add response interceptor for debugging
apiClient.interceptors.response.use(
  (response) => {
    console.log('API Response:', response.status, response.config.url);
    console.log('Response data:', response.data);
    return response;
  },
  (error) => {
    console.error('API Error:', error.response?.status, error.config?.url);
    console.error('Error details:', error.response?.data || error.message);
    return Promise.reject(error);
  }
);

// API service methods
export const apiService = {
  // Repository methods
  async getRepositories(token: string): Promise<Repository[]> {
    console.log('Getting repositories with token:', token ? 'Token exists' : 'No token');
    console.log('API_BASE_URL:', API_BASE_URL);
    
    // Temporary: Return mock data if API fails
    try {
      const response = await apiClient.get('/api/repositories', {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });
      console.log('Repository API response:', response.data);
      return response.data.repositories || [];
    } catch (error) {
      console.error('Repository API error:', error);
      console.warn('Using mock data for development');
      
      // Return mock repositories for testing
      return [
        {
          id: 1,
          name: 'test-repo',
          full_name: 'user/test-repo',
          description: 'A test repository',
          language: 'JavaScript',
          stargazers_count: 5,
          forks_count: 2,
          updated_at: new Date().toISOString(),
          html_url: 'https://github.com/user/test-repo',
          private: false,
          size: 100,
          default_branch: 'main'
        },
        {
          id: 2,
          name: 'another-repo',
          full_name: 'user/another-repo',
          description: 'Another test repository',
          language: 'TypeScript',
          stargazers_count: 10,
          forks_count: 3,
          updated_at: new Date().toISOString(),
          html_url: 'https://github.com/user/another-repo',
          private: false,
          size: 200,
          default_branch: 'main'
        }
      ];
    }
  },

  // Analysis methods
  async analyzeRepository(repositoryFullName: string, token: string): Promise<AnalysisResult> {
    const response = await apiClient.post(`/api/repositories/${repositoryFullName}/analyze`, null, {
      params: { token },
    });
    return response.data;
  },

  // Get analysis status
  async getAnalysisStatus(analysisId: string): Promise<AnalysisResult> {
    const response = await apiClient.get(`/api/analysis/${analysisId}`);
    return response.data;
  },

  // Get repository analysis history
  async getRepositoryAnalyses(repositoryId: string): Promise<AnalysisResult[]> {
    const response = await apiClient.get(`/api/repositories/${repositoryId}/analyses`);
    return response.data;
  },

  // Auth methods (for future use when we implement proper auth)
  async getCurrentUser(token: string) {
    // TODO: Implement when we have proper JWT auth
    return null;
  },
};

export default apiService;
